import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CalendarClock, ChevronRight, ClipboardCheck, FileText, ShieldCheck } from 'lucide-react';
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import type { OpenItem, OpenItemList, OpenItemSource } from '@up2d8/contracts';
import { Card, cn } from '@up2d8/ui';
import { useSession } from '../../shared/session';

/**
 * De kaart "Voor jou" in de rechterkolom (replica-spec §10.1).
 *
 * Eén lijst met wat er voor deze medewerker open staat, uit vier modules samen. De app weet niet
 * welke vier dat zijn en hoort dat ook niet te weten: ze vraagt `GET /api/v1/me/open-items` en
 * tekent wat er terugkomt. Staat een module uit, dan komen haar regels niet mee — zonder lege plek
 * en zonder foutmelding (ADR-0066).
 *
 * De kaart verdwijnt bij nul regels. Een kaart die "0 open" zegt, vraagt aandacht voor niets, en
 * dat is precies hoe een paneel iets wordt dat je niet meer leest.
 */

/**
 * Een `href` van de server als `to` + `search` voor `<Link>`.
 *
 * De router leest geen querystring uit `to`: `/app/formulieren?formulier=…` werd een pad dat niet
 * bestaat, en het formulier ging niet open. Daarom de query apart als `search`.
 */
export function openItemLink(href: string): { to: string; search?: Record<string, string> } {
  const at = href.indexOf('?');
  if (at === -1) return { to: href };
  return {
    to: href.slice(0, at),
    search: Object.fromEntries(new URLSearchParams(href.slice(at + 1))),
  };
}

/** Het icoon per bron, met de kleurparen uit §10.1. */
const ICON: Record<
  OpenItemSource,
  { icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>; classes: string }
> = {
  reach: { icon: ShieldCheck, classes: 'bg-brand-primary-tint text-brand-primary-dark' },
  forms: { icon: FileText, classes: 'bg-brand-primary-tint text-brand-primary-dark' },
  tasks: { icon: ClipboardCheck, classes: 'bg-brand-orange-tint text-brand-orange-deep' },
  agenda: { icon: CalendarClock, classes: 'bg-brand-dark-blue-tint text-brand-dark-blue' },
};

function OpenItemRow({ item }: { item: OpenItem }) {
  const { icon: Icon, classes } = ICON[item.source];

  return (
    <Link
      {...openItemLink(item.href)}
      data-testid="open-item"
      data-source={item.source}
      data-urgent={item.urgent ? 'ja' : 'nee'}
      className="flex items-center gap-2.5 rounded-lg p-2.5 transition-colors hover:bg-background"
    >
      <span
        aria-hidden
        className={cn(
          'flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-md',
          classes,
        )}
      >
        <Icon className="h-4 w-4" />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate font-heading text-copy font-medium leading-[1.25] text-text-primary">
          {item.title}
        </span>
        {/*
          Dringend is oranje en vet, en dat is de enige plek waar de metaregel van kleur verandert.

          Urgentie komt van een datum die nadert en niet van wat een module vindt (ADR-0066): de
          server rekent ze uit `dueAt`. Zou elke module zelf "dringend" mogen roepen, dan staat
          binnen een half jaar de hele lijst in het oranje — en dan betekent oranje niets meer.
        */}
        <span
          className={cn(
            'block truncate text-caption leading-[1.25]',
            item.urgent ? 'font-heading font-semibold text-brand-orange-deep' : 'text-text-muted',
          )}
        >
          {item.meta}
        </span>
      </span>

      <ChevronRight aria-hidden className="h-3.5 w-3.5 shrink-0 text-text-muted" />
    </Link>
  );
}

export function AppOpenItems() {
  const { t } = useTranslation();
  const { api } = useSession();

  const openItems = useQuery({
    queryKey: ['open-items'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/me/open-items', {});
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as OpenItemList).items;
    },
  });

  const items = openItems.data ?? [];
  if (items.length === 0) return null;

  return (
    <Card compact data-testid="open-items" className="p-[18px]">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-heading text-card-title font-semibold text-text-primary">
          <span
            aria-hidden
            className="flex h-[26px] w-[26px] items-center justify-center rounded-sm bg-brand-orange-tint text-brand-orange-deep"
          >
            <ClipboardCheck className="h-3.5 w-3.5" strokeWidth={2.5} />
          </span>
          {t('openItems.title')}
        </h2>
        {/* De teller telt de regels en komt niet uit een apart getal: twee bronnen voor hetzelfde
            aantal betekent dat er een dag komt waarop ze niet meer gelijk zijn. */}
        <span
          data-testid="open-items-count"
          className="shrink-0 rounded-pill bg-brand-orange px-2 py-0.5 font-heading text-micro font-bold tracking-normal text-text-on-dark"
        >
          {t('openItems.count', { count: items.length })}
        </span>
      </div>

      <div className="flex flex-col gap-0.5">
        {items.map((item) => (
          <OpenItemRow key={item.key} item={item} />
        ))}
      </div>
    </Card>
  );
}
